import React, { useState, useCallback, useMemo, useEffect } from 'react';
import Modal from '../../components/Modal';

import CustomInput from '../../components/CustomInput';
import ActionButtons from '../../components/ActionButtons';
import { createUserValidator } from './validator';
import CustomSelect from '../../components/CustomSelect';
import api from '../../api';
import { useQuery } from '@tanstack/react-query';

const INITIAL_FORM_STATE = {
    firstname: '',
    lastname: '',
    email: '',
    password: '',
    phone: '',
    sex: '',
    region: '',
    district: '',
    tin: 'string',
    country: 'Tanzania',
    role: ''
};

const INITIAL_ERROR_STATE = {
    firstname: '',
    lastname: '',
    email: '',
    password: '',
    phone: '',
    sex: '',
    region: '',
    district: '',
    tin: 'String',
    country: 'Tanzania',
    role: ''
};

const CreateUserModal = ({ isOpen, onClose, isEdit = false, selectedItem, setIsEdit, onSubmit, status }) => {
    const [showPassword, setShowPassword] = useState(false);

    // Initialize form data
    const [formData, setFormData] = useState(INITIAL_FORM_STATE);
    const [errors, setErrors] = useState(INITIAL_ERROR_STATE);

    // Get all roles
    const { data: roles } = useQuery({
        queryKey: ['roles'],
        queryFn: () => api.roles.getRoles(),
        onError: (error) => {
            console.log(error);
        },
        select: (data) => data?.data?.data
    });

    // Handle input change with validation
    const handleInputChange = useCallback((name, value) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
    }, []);

    // Validate all fields
    const validateForm = useCallback(() => {
        const newErrors = {};
        let isValid = true;

        Object.keys(formData).forEach((key) => {
            // Skip password validation if in edit mode
            if (isEdit && key === 'password') {
                return; // Skip this iteration
            }

            // Only validate password in create mode or if password is provided in edit mode
            if (key === 'password' && isEdit && !formData[key]) {
                return; // Skip empty password validation in edit mode
            }

            const error = createUserValidator(key, formData[key]);
            newErrors[key] = error;
            if (error) isValid = false;
        });

        setErrors(newErrors);

        return isValid;
    }, [formData, isEdit]);

    // Reset form when modal opens/closes or when edit mode/selected item changes
    useEffect(() => {
        if (isOpen && isEdit && selectedItem) {
            setFormData({
                firstname: selectedItem?.firstName || '',
                lastname: selectedItem?.lastName || '',
                email: selectedItem?.email || '',
                password: '', // Don't populate password in edit mode
                country: selectedItem?.country || '',
                // tin: selectedItem?.tin || '',
                region: selectedItem?.region || '',
                district: selectedItem?.district || '',
                role: selectedItem?.role || '',
                sex: selectedItem?.sex || '',
                phone: selectedItem?.phone || ''
            });
        } else if (!isOpen) {
            setFormData(INITIAL_FORM_STATE);
            setShowPassword(false);
            setIsEdit(false);
        }
    }, [isOpen, isEdit, selectedItem, setIsEdit]);

    const togglePasswordVisibility = useCallback(() => {
        setShowPassword((prev) => !prev);
    }, []);

    // Form configuration
    const formConfig = useMemo(
        () => [
            {
                formControl: 'input',
                autoComplete: 'off',
                // label: 'First Name',
                id: 'firstname',
                placeholder: 'Enter First Name',
                name: 'firstname',
                type: 'text',
                required: true,
                value: formData.firstname,
                onChange: (e) => handleInputChange('firstname', e.target.value),
                // onBlur: () => handleBlur('firstname'),
                error: errors.firstname
            },
            {
                formControl: 'input',
                autoComplete: 'off',
                // label: 'Last Name',
                id: 'lastname',
                placeholder: 'Enter Last Name',
                name: 'lastname',
                type: 'text',
                required: true,
                value: formData.lastname,
                onChange: (e) => handleInputChange('lastname', e.target.value),

                error: errors.lastname
            },
            {
                formControl: 'input',
                autoComplete: 'off',
                // label: 'Email',
                id: 'email',
                placeholder: 'Enter Valid Email',
                name: 'email',
                type: 'email',
                required: true,
                value: formData.email,
                onChange: (e) => handleInputChange('email', e.target.value),
                error: errors.email
            },
            {
                formControl: 'input',
                autoComplete: 'off',
                // label: 'Password',
                id: 'password',
                placeholder: 'Enter Password',
                name: 'password',
                type: 'password',
                required: !isEdit,
                value: formData.password,
                onChange: (e) => handleInputChange('password', e.target.value),
                hint: 'Password must contain at least 8 characters, including uppercase, lowercase, numbers and special characters',
                isToogleButton: true,
                minLength: 8,
                pattern: '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&])[A-Za-z\\d@$!%*?&]{8,}$',
                error: errors.password
            },
            {
                formControl: 'input',
                autoComplete: 'off',
                // label: 'Phone Number',
                id: 'phone',
                placeholder: 'Enter Phone Number',
                name: 'phone',
                type: 'text',
                required: true, // Password is required only for new users
                value: formData.phone,
                onChange: (e) => handleInputChange('phone', e.target.value),
                // hint: 'Phone number must contain at least 8 characters, including uppercase, lowercase, numbers and special characters',
                // isToogleButton: true,
                minLength: 8,
                pattern: '^[0-9]{10}$',
                // onBlur: () => handleBlur('phone'),
                error: errors.phone
            },
            {
                formControl: 'select',
                autoComplete: 'off',
                // label: 'Phone Number',
                id: 'sex',
                placeholder: 'Enter Sex',
                name: 'sex',
                type: 'text',
                required: true, // Password is required only for new users
                value: formData.sex,
                onChange: handleInputChange,
                options: [
                    { id: 'Male', label: 'Male' },
                    { id: 'Female', label: 'Female' }
                ],
                error: errors.sex
            },
            {
                formControl: 'input',
                autoComplete: 'off',
                id: 'region',
                placeholder: 'Enter Region',
                name: 'region',
                type: 'text',
                required: true, // Password is required only for new users
                value: formData.region,
                onChange: (e) => handleInputChange('region', e.target.value),
                error: errors.region
            },
            {
                formControl: 'input',
                autoComplete: 'off',
                id: 'district',
                placeholder: 'Enter District',
                name: 'district',
                type: 'text',
                required: true, // Password is required only for new users
                value: formData.district,
                onChange: (e) => handleInputChange('district', e.target.value),
                error: errors.district
            },
            {
                formControl: 'select',
                autoComplete: 'off',
                id: 'role',
                placeholder: 'Enter Role',
                name: 'role',
                type: 'text',
                required: true, // Password is required only for new users
                value: formData.role,
                options: roles?.map((role) => ({ id: role.id, label: role.name })),
                onChange: handleInputChange,
                error: errors.role
            }
        ],
        [formData, handleInputChange, errors, isEdit, roles]
    );

    // Handle form submission
    const handleSubmit = useCallback(
        (e) => {
            e.preventDefault();
            // if (!validateForm()) {
            //     return;
            // }

           
            if (typeof onSubmit === 'function') {
                console.log('is a function');
                if (isEdit) {
                    const updateData = { ...formData, id: selectedItem?.id };
                    onSubmit(updateData);
                }
                onSubmit(formData);
            }
        },
        [isEdit, formData, onSubmit, selectedItem]
    );
    // Memoize labels
    const labels = useMemo(
        () => ({
            normalLabel: isEdit ? 'Update User' : 'Create User',
            loadingLabel: isEdit ? 'Updating...' : 'Creating...'
        }),
        [isEdit]
    );

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? 'UPDATE USER' : 'CREATE NEW USER'}>
            <form onSubmit={handleSubmit} className="space-y-4">
                {formConfig.map((item) => {
                    const isEditMode = isEdit && item.name === 'password';
                    if (item.formControl === 'select') {
                        return (
                            <CustomSelect
                                key={item.id}
                                item={{
                                    ...item,
                                    error: errors[item.name] || item.error
                                }}
                            />
                        );
                    }
                    if (!isEditMode) {
                        return <CustomInput key={item.id} item={item} showPassword={showPassword} togglePasswordVisibility={togglePasswordVisibility} />;
                    }
                    return null;
                })}
                <ActionButtons status={status} onClose={onClose} onSubmit={handleSubmit} isEdit={isEdit} label={labels} />
            </form>
        </Modal>
    );
};

export default React.memo(CreateUserModal);
